import { markSet } from "./cell-marks";
import type {
  ComparisonState,
  ComparisonSheet,
  ComparisonGrid,
  EnrichedComparisonState,
  EnrichedComparisonSheet,
  CellMark,
} from "./types";

export const enrichComparison = (
  state: ComparisonState,
): EnrichedComparisonState => ({
  ...state,
  changedSheets: state.sheets.filter(
    (sheet) => sheet.state !== "unchanged" || !!sheet.renamed_from,
  ),
  pickerPeriods: state.periods.map((period) => ({
    period_start: period.start_date,
    period_end: period.end_date,
    has_payroll_run: period.ready === true,
    latest_icm_run_status: null,
    is_locked: false,
  })),
});

const indexCells = (grid: ComparisonGrid | null) =>
  new Map(
    (grid?.cells ?? []).map((cell) => [`${cell.row}:${cell.col}`, cell.value]),
  );

export const enrichComparisonSheet = (
  sheet: ComparisonSheet,
): EnrichedComparisonSheet => {
  const marks = new Map<string, CellMark>();
  markSet(sheet.format_changed, "format", marks);
  markSet(sheet.added, "added", marks);
  markSet(sheet.removed, "removed", marks);
  markSet(sheet.value_changed, "changed", marks);
  const counts: Record<CellMark, number> = {
    changed: 0,
    added: 0,
    removed: 0,
    format: 0,
  };
  marks.forEach((mark) => {
    counts[mark] += 1;
  });
  return {
    ...sheet,
    marks,
    counts,
    changes: Array.from(marks.keys(), (key) => key.split(":").map(Number)).sort(
      (a, b) => a[0] - b[0] || a[1] - b[1],
    ),
    sides: {
      current: indexCells(sheet.current),
      proposed: indexCells(sheet.proposed),
    },
    rows: Math.max(sheet.current?.rows ?? 0, sheet.proposed?.rows ?? 0),
    cols: Math.max(sheet.current?.cols ?? 0, sheet.proposed?.cols ?? 0),
  };
};
